/**
 * Web Push 能力探测 / 权限模型 / VAPID 公钥解码 / 角标计数（任务 18a）。
 *
 * 纯模型：不碰 window、不发请求、不在模块级读全局——全部能力经 `PushEnv` 注入，
 * 浏览器侧由 `browserPushEnv()` 一次性采集（见 docs/push.md）。这样 18b 的 SW 侧
 * 与 18c 的设置页接线共用同一套判定，且本文件可在 node 环境直接单测。
 *
 * 口径要点（踩过的坑都写在这里，别再去翻规范）：
 * - **iOS 只在主屏 Web App 内暴露 Push API**：Safari 标签页里 `window.PushManager`
 *   与 `window.Notification` 根本不存在（iOS 16.4+ 亦然）。因此 iOS 上「缺能力」
 *   不等于「这台机器不支持」，要判成 `needs-install` 引导去 /install（任务 16b），
 *   而不是冷冰冰的「不支持」。
 * - **requestPermission 必须在用户点击的调用栈里**（PROMPT 硬性要求：不能自动弹）。
 *   模型只给 `canRequestPermission` 判定，是否处在手势里由调用方（18c 的开关）保证。
 * - 老 Safari 的 `Notification.requestPermission` 只有**回调版**、不返回 Promise，
 *   `requestNotificationPermission()` 把两种形态统一成 Promise。
 * - 经描述文件安装的 Web Clip 里推送与角标是否真的可用，**仍待真机验证**
 *   （`docs/ios-webclip.md` §5 清单）。所以所有能力调用一律失败静默降级，
 *   不让不可用的环境抛错或卡流程。
 */

import { INSTALL_PATH, detectInstallHost, type InstallOs } from '~/shell/installGuideModel';
import { BRAND_NAME } from '~/brand';

/** `Notification.permission` 的三态（注入时做 normalize，不信任原始字符串） */
export type NotifyPermission = 'default' | 'granted' | 'denied';

/** 能力快照：一次采集、同帧内多处判定共用 */
export interface PushEnv {
  /** `'serviceWorker' in navigator` */
  readonly hasServiceWorker: boolean;
  /** `'PushManager' in window` */
  readonly hasPushManager: boolean;
  /** `'Notification' in window` */
  readonly hasNotification: boolean;
  /** `'setAppBadge' in navigator` */
  readonly hasBadge: boolean;
  /** 无 Notification 时按 'default' 填 */
  readonly permission: NotifyPermission;
  /** 主屏全屏运行（Web Clip / 已安装 PWA），取自 layoutEnv.isStandalone */
  readonly isStandalone: boolean;
  /** 复用安装引导页的单点 UA 判定 */
  readonly os: InstallOs;
  /** 非安全上下文（http 直连）下 SW 与 Push 一概不可用 */
  readonly isSecureContext: boolean;
}

/**
 * 推送可用性：
 * - `ready` 已授权，可订阅、可展示通知
 * - `prompt` 环境支持但未授权 → 等用户点击后请求权限
 * - `denied` 用户拒过 → **不得再次请求**（浏览器也不会再弹），只能引导去系统设置
 * - `needs-install` iOS 浏览器标签页 → 先装到主屏才有推送能力
 * - `unsupported` 环境根本没有能力（含非安全上下文）
 */
export type PushSupport = 'ready' | 'prompt' | 'denied' | 'needs-install' | 'unsupported';

/** 单点判定：这个环境的推送处于哪一档 */
export function pushSupportOf(env: PushEnv): PushSupport {
  if (!env.isSecureContext) return 'unsupported';
  if (!(env.hasServiceWorker && env.hasPushManager && env.hasNotification)) {
    // iOS 标签页缺能力是「没装到主屏」而非不支持（见文件头）；已 standalone 还缺就是真不支持
    return env.os === 'ios' && !env.isStandalone ? 'needs-install' : 'unsupported';
  }
  if (env.permission === 'granted') return 'ready';
  if (env.permission === 'denied') return 'denied';
  return 'prompt';
}

/** 设置页/提示条要的全部派生信息，一次算完 */
export interface PushCapability {
  readonly support: PushSupport;
  /** 可以订阅并展示通知 */
  readonly enabled: boolean;
  /** 现在调 requestPermission() 才有意义（必须放在用户点击里） */
  readonly canRequestPermission: boolean;
  /** 角标可用（与通知权限的关系见 canBadgeIn 注释） */
  readonly canBadge: boolean;
  /** 设置页该行的副标题文案 */
  readonly hint: string;
  /** 非 null 时给个「去安装」入口（仅 needs-install） */
  readonly installPath: string | null;
}

const HINTS: Readonly<Record<PushSupport, string>> = {
  ready: '已开启，离线时新消息会推送通知',
  prompt: '需要你允许浏览器通知后才能收到推送',
  denied: '通知权限已被拒绝，请在系统设置中允许本站通知后重试',
  'needs-install': `把 ${BRAND_NAME} 添加到主屏幕后才能接收推送通知`,
  unsupported: '当前浏览器不支持推送通知',
};

/**
 * 角标是否可用。
 *
 * iOS 的主屏 Web App 上 `setAppBadge` 依赖通知权限（未授权时 reject），
 * 桌面 Chrome/Edge 的已安装 PWA 不需要权限即可设角标。按此分档，
 * 调用侧仍走 `applyAppBadge` 的失败静默，判错也只是少个角标。
 */
export function canBadgeIn(env: PushEnv): boolean {
  if (!env.hasBadge) return false;
  return env.os !== 'ios' || env.permission === 'granted';
}

export function pushCapability(env: PushEnv): PushCapability {
  const support = pushSupportOf(env);
  return {
    support,
    enabled: support === 'ready',
    canRequestPermission: support === 'prompt',
    canBadge: canBadgeIn(env),
    hint: HINTS[support],
    installPath: support === 'needs-install' ? INSTALL_PATH : null,
  };
}

/** 不信任外部字符串：非三态一律按 default（最保守：还会去问用户） */
export function normalizeNotifyPermission(raw: unknown): NotifyPermission {
  return raw === 'granted' || raw === 'denied' ? raw : 'default';
}

/** `browserPushEnv` 的注入面：测试里给纯对象，浏览器里给真全局 */
export interface PushEnvSource {
  readonly userAgent: string;
  readonly isSecureContext: boolean;
  readonly isStandalone: boolean;
  /** navigator（只看两个 key 是否存在） */
  readonly nav: { readonly serviceWorker?: unknown; readonly setAppBadge?: unknown };
  /** window.Notification，不存在时 undefined */
  readonly notification?: { readonly permission?: unknown } | undefined;
  /** window.PushManager，不存在时 undefined */
  readonly pushManager?: unknown;
}

/** 采集能力快照。isStandalone 由调用方从 layoutEnv 传入（那里是单点来源）。 */
export function readPushEnv(src: PushEnvSource): PushEnv {
  return {
    hasServiceWorker: src.nav.serviceWorker !== undefined,
    hasPushManager: src.pushManager !== undefined,
    hasNotification: src.notification !== undefined,
    hasBadge: typeof src.nav.setAppBadge === 'function',
    permission: normalizeNotifyPermission(src.notification?.permission),
    isStandalone: src.isStandalone,
    os: detectInstallHost(src.userAgent).os,
    isSecureContext: src.isSecureContext,
  };
}

/** 浏览器侧的便利封装（薄到不需要单测；判定全在 readPushEnv/pushSupportOf） */
export function browserPushEnv(isStandalone: boolean): PushEnv {
  const win = window as Window & { PushManager?: unknown };
  return readPushEnv({
    userAgent: navigator.userAgent,
    isSecureContext: window.isSecureContext,
    isStandalone,
    nav: navigator as unknown as PushEnvSource['nav'],
    notification: 'Notification' in win ? (Notification as unknown as { permission?: unknown }) : undefined,
    pushManager: win.PushManager,
  });
}

/** `Notification.requestPermission` 的两种形态：回调版（老 Safari）/ Promise 版 */
export type PermissionRequestFn = (callback: (permission: unknown) => void) => unknown;

/**
 * 统一成 Promise，且**恒 resolve**（抛错/reject 都按 denied 处理——调用方只想知道
 * 结果，不该为「权限 API 自己炸了」写一遍 catch）。
 *
 * 必须在用户点击的调用栈里调用，模型不做这项检查（无法检查）。
 */
export function requestNotificationPermission(
  request: PermissionRequestFn,
): Promise<NotifyPermission> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (raw: unknown): void => {
      if (settled) return;
      settled = true;
      resolve(normalizeNotifyPermission(raw));
    };
    let returned: unknown;
    try {
      // 同时传回调并看返回值：两种形态的浏览器都能收到结果，settled 保证只认第一个
      returned = request(done);
    } catch {
      done('denied');
      return;
    }
    if (returned !== null && typeof returned === 'object' && 'then' in returned) {
      void (returned as Promise<unknown>).then(done, () => done('denied'));
    }
  });
}

const B64URL_CHARS = /^[A-Za-z0-9_-]+$/;

/**
 * base64url → 字节。`pushManager.subscribe({ applicationServerKey })` 只吃
 * ArrayBuffer/Uint8Array，VAPID 公钥是 base64url 文本，必须自己解。
 * 输入非法返回 null（配置写错时让调用方跳过订阅，而不是抛进 subscribe）。
 */
export function urlBase64ToUint8Array(input: string): Uint8Array | null {
  const body = input.trim().replace(/=+$/, '');
  if (body === '' || !B64URL_CHARS.test(body)) return null;
  // 长度 %4===1 的 base64 不可能存在（1 个字符解不出整字节）
  if (body.length % 4 === 1) return null;
  const padded = body.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (body.length % 4)) % 4);
  let raw: string;
  try {
    raw = atob(padded);
  } catch {
    return null;
  }
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

/** VAPID 公钥 = P-256 未压缩点：恰 65 字节且首字节 0x04。配置写错时提前发现。 */
export function isVapidPublicKey(bytes: Uint8Array | null): bytes is Uint8Array {
  return bytes !== null && bytes.length === 65 && bytes[0] === 0x04;
}

/** 解码 + 校验，任一步不过返回 null（调用方据此跳过订阅并记一条告警） */
export function vapidApplicationServerKey(base64Url: string): Uint8Array | null {
  const bytes = urlBase64ToUint8Array(base64Url);
  return isVapidPublicKey(bytes) ? bytes : null;
}
